// Action Required surface: a floating "help bubble" that appears at the top of the
// stacking order ONLY when agent runs are awaiting a human. Like an Intercom/chat
// bubble — it floats above everything (the Designer's test panel at z-index 1000 and
// the inbox blade at 1100/1101), draws the eye with a bright-red count badge, toggles
// the inbox blade open/closed on click, and DISAPPEARS once the last item is resolved.
// Living on top of the chrome (rather than inside the header/sidebar) sidesteps the
// modality problem: a full-screen test overlay can't bury it. Replaces the former
// header icon button.
//
// The badge count is DB-authoritative, driven by the shell's observer agent
// SignalR connection (see routes.tsx: `<AgentConnectionProvider observer>`), so a
// newly parked run surfaces in real time without pinning runners hot. The DB is
// the source of truth: re-query the count on every (re)connect (the epoch bump
// recovers anything missed while disconnected), on any live agent event, and as a
// SignalR-down fallback on navigation. While the blade is open, InboxPanel's
// onCountChange keeps the badge in lockstep — including right after a response
// resolves an item. The count includes Orion Pulsars proposal batches and
// questions when that feature is on (lib/actionItems).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SvgIcon } from '@progress/kendo-react-common';
import { inboxFilledIcon } from './headerIcons';
import { useAgentActivity } from '../lib/agentActivity';
import { actionItemCount, loadActionItems } from '../lib/actionItems';
import { Blade } from './Blade';
import { InboxPanel } from '../pages/Inbox/InboxPanel';

export function ActionRequiredBubble() {
  const agent = useAgentActivity();
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  // Monotonic token so only the LATEST inbox fetch applies its count. refreshCount
  // fires from several triggers (mount, reconnect, agent events, navigation) that
  // can overlap; without this guard a slower older response could clobber a newer
  // one and leave the badge stale until the next trigger.
  const refreshSeqRef = useRef(0);
  // Highest sequence whose response has actually been APPLIED. Guarding on this
  // (rather than the latest *issued* seq) means a newer fetch that FAILS can't
  // suppress an older fetch that SUCCEEDED — the badge reflects the newest data
  // that actually came back, not whichever request happened to be issued last.
  const appliedSeqRef = useRef(0);

  // Apply a fresh count. When it hits zero the bubble unmounts (see the early
  // return below), so also drop `open` — otherwise a blade left open while the
  // last item resolves would silently reappear if a new item arrives later. Done
  // here (in the setter), not in an effect, to avoid a setState-in-effect cascade.
  const applyCount = useCallback((next: number) => {
    setCount(next);
    if (next === 0) setOpen(false);
  }, []);

  const refreshCount = useCallback(() => {
    const seq = ++refreshSeqRef.current;
    loadActionItems()
      .then((items) => {
        // Apply only if this is newer than the last successfully-applied response.
        if (seq > appliedSeqRef.current) {
          appliedSeqRef.current = seq;
          applyCount(actionItemCount(items));
        }
      })
      .catch(() => {});
  }, [applyCount]);

  // First load + each fresh connection. `agent` is null only outside the provider
  // (e.g. unit tests); the effect still runs once on mount via `refreshCount`.
  useEffect(() => {
    refreshCount();
  }, [refreshCount, agent?.connectionEpoch]);

  // Any live agent event (e.g. a run entering NeedsInput/NeedsApproval) re-queries.
  useEffect(() => agent?.onAgentActivity(() => refreshCount()), [agent, refreshCount]);

  // SignalR-down fallback: re-query on navigation. Skip the initial mount run — the
  // connection effect above already covers first load — so we don't double-fetch.
  const didMountPathRef = useRef(false);
  useEffect(() => {
    if (!didMountPathRef.current) {
      didMountPathRef.current = true;
      return;
    }
    refreshCount();
  }, [location.pathname, refreshCount]);

  // Backup poll. SignalR is only a NUDGE to re-query; the DB is the source of truth.
  // A push can be missed — a dropped/recovering connection, or (in dev) a runner
  // callback that lands on a different API instance sharing the same tenant DB — and
  // without a navigation there's no other trigger, so the badge would sit stale even
  // though the item is in the database. A slow interval re-query closes that gap. It
  // pauses while the tab is hidden (no point polling a backgrounded tab) and catches
  // up immediately when the tab becomes visible again.
  useEffect(() => {
    const BACKUP_MS = 30_000;
    let timer: number | undefined;
    const start = () => {
      if (timer === undefined) timer = window.setInterval(refreshCount, BACKUP_MS);
    };
    const stop = () => {
      if (timer !== undefined) {
        window.clearInterval(timer);
        timer = undefined;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        refreshCount();
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshCount]);

  // Nothing awaiting a human → no surface at all (the bubble only appears when
  // needed). `applyCount` already drops `open` whenever the count reaches zero.
  if (count === 0) return null;

  const label = `Action Required, ${count} awaiting response`;

  return (
    <>
      <button
        type="button"
        className="action-bubble"
        aria-label={label}
        title="Action Required"
        onClick={() => setOpen((v) => !v)}
      >
        <SvgIcon icon={inboxFilledIcon} />
        <span className="action-bubble-count" aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      </button>
      {open && (
        <Blade
          title="Action Required"
          ariaLabel="Action Required"
          width="min(720px, 96vw)"
          onClose={() => setOpen(false)}
        >
          <InboxPanel onCountChange={applyCount} onNavigate={() => setOpen(false)} />
        </Blade>
      )}
    </>
  );
}

export default ActionRequiredBubble;
